import { COLUMN_NAME_ID } from '../../../constants';

export const LineItemColumn = Object.freeze({
  Alert: 'Alert',
  Name: COLUMN_NAME_ID,
  CreateAd: 'CreateAd',
  Country: 'Country',
  DeliverableLineItemPackageName: 'DeliverableLineItemPackageName',
  TrackingId: 'TrackingId',
  ParentOmsLinkId: 'ParentOmsLinkId',
  BillingCalendar: 'BillingCalendar',
  Status: 'Status',
  Review: 'Review',
  ScheduleStart: 'ScheduleStart',
  ActualStartDate: 'ActualStartDate',
  ScheduleEnd: 'ScheduleEnd',
  DisplayPublisherTarget: 'DisplayPublisherTarget',
  ScheduleTimezoneOffset: 'ScheduleTimezoneOffset',
  GeneralPacingStatus: 'GeneralPacingStatus',
  Goal: 'Goal',
  MetricsCompletions: 'MetricsCompletions',
  MetricsScheduledPercentPacing: 'MetricsScheduledPercentPacing',
  MetricsPercentPacing: 'MetricsPercentPacing',
  MetricsPercentExpected: 'MetricsPercentExpected',
  MetricsPercentCompletions: 'MetricsPercentCompletions',
  PacingType: 'PacingType',
  Tier: 'Tier',
  YodaThreshold: 'YodaThreshold',
  YodaPacingStatus: 'YodaPacingStatus',
  PriorityName: 'PriorityName',
  PriorityValue: 'PriorityValue',
  AdProduct: 'AdProduct',
  FrequencyCapList: 'frequencyCapList',
  BrandCapping: 'brandCapping',
  AutomateBuffer: 'AutomateBuffer',
  DeliveryBufferPercent: 'DeliveryBufferPercent',
  FrontLoadPercent: 'FrontLoadPercent',
  DeliveryForensics: 'deliveryForensics',
  AccessorId: 'accessorId',
  MetricsRemainingExpectedCompletionsAcc: 'MetricsRemainingExpectedCompletionsAcc',
  MetricsCompletionsAtRisk: 'MetricsCompletionsAtRisk',
  MetricsImpressions: 'MetricsImpressions',
  Revenue: 'Revenue',
  MetricsRevenueAtRisk: 'MetricsRevenueAtRisk',
  BillableThirdParty: 'BillableThirdParty',
  BillableEvent: 'BillableEvent',
  UnitCost: 'UnitCost',
  MetricsUnderPacingRevenue: 'MetricsUnderPacingRevenue',
  LineOmsLinkId: 'LineOmsLinkId',
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  CreativeMaxSeconds: 'CreativeMaxSeconds',
  CampaignName: 'CampaignName',
  DeliveryGoal: 'DeliveryGoal',
  IsCoppaOrCaru: 'IsCoppaOrCaru',
  IsAddedValue: 'IsAddedValue',
  IsMakegood: 'IsMakegood',
  IsFiller: 'IsFiller',
  DeliverableLineItemBuyerName: 'DeliverableLineItemBuyerName',
  DeliverableLineItemBuyerId: 'DeliverableLineItemBuyerId',
  DspName: 'DspName',
  DealId: 'DealId',
  SSP: 'SSP',
  CampaignTraffickerEmail: 'CampaignTraffickerEmail',
  CampaignOrderSalespersonEmail: 'CampaignOrderSalespersonEmail',
  Currency: 'Currency',
  Sequence: 'Sequence',
  CustomPacingApplied: 'CustomPacingApplied',
  CustomPacingEligible: 'CustomPacingEligible',
  CustomPacingRequested: 'CustomPacingRequested',
  CostMethod: 'CostMethod',
  DistributionPartnerList: 'DistributionPartnerList',
});

export const LineItemColumnMeta: Record<string, { header: string; tooltip?: string; subHeader?: string }> = {
  [LineItemColumn.Alert]: {
    header: 'Alert',
    tooltip: 'An icon in this column indicates an issue with the line item. Hover over the icon for more information.',
  },
  [LineItemColumn.Name]: {
    header: 'Line Item Name',
    tooltip: 'The name of the line item as booked in Salesforce/Operative.',
  },
  [LineItemColumn.CreateAd]: {
    header: 'Create Ad',
    tooltip: 'Click the plus sign to create a new ad within the line item.',
  },
  [LineItemColumn.Country]: {
    header: 'Country',
    tooltip: 'The countries where the line item is eligible to serve.',
  },
  [LineItemColumn.DeliverableLineItemPackageName]: {
    header: 'OMS Package Name',
    tooltip: 'The parent package name as shown in Operative.',
  },
  [LineItemColumn.TrackingId]: {
    header: 'Tracking ID',
    tooltip:
      '16 digit identifier for the line item used for external purposes and shared with 3rd-party vendors, gets passed via our macros.',
  },
  [LineItemColumn.ParentOmsLinkId]: {
    header: 'Parent OMS ID',
    tooltip: 'The parent package ID as shown in Operative.',
  },
  [LineItemColumn.BillingCalendar]: {
    header: 'Billing Calendar',
    tooltip: 'Indicates whether campaign is billed using calendar month (Gregorian) or broadcast month.',
  },
  [LineItemColumn.Status]: {
    header: 'Status',
    tooltip: 'Indicates whether the campaign is active or not.',
  },
  [LineItemColumn.Review]: {
    header: 'Review',
    tooltip: 'Campaign review status for the line.',
  },
  [LineItemColumn.ScheduleStart]: {
    header: 'Start Date',
    subHeader: 'mm/dd/yyyy 00:00 (24 hour clock)',
    tooltip: 'The date the line item can begin delivery.',
  },
  [LineItemColumn.ActualStartDate]: {
    header: 'Actual Start Date',
    subHeader: 'mm/dd/yyyy 00:00 (24 hour clock)',
    tooltip:
      "The date the line item began delivering, displayed in the Line Item's local timezone. The table will populate this value once the line reaches 200 completions.",
  },
  [LineItemColumn.ScheduleEnd]: {
    header: 'End Date',
    subHeader: 'mm/dd/yyyy 00:00 (24 hour clock)',
    tooltip: 'The date at which the line item will stop delivering ads.',
  },
  [LineItemColumn.DisplayPublisherTarget]: {
    header: 'Publisher',
    tooltip: 'The name of the app(s) that will run the ad.',
  },
  [LineItemColumn.ScheduleTimezoneOffset]: {
    header: 'Time Zone',
    tooltip: 'The time zone for the line item date range.',
  },
  [LineItemColumn.GeneralPacingStatus]: {
    header: 'General Pacing Status',
    tooltip: 'Indicates whether the line item is on schedule to fulfill the delivery goal.',
  },
  [LineItemColumn.Goal]: {
    header: 'Contracted Goal',
    tooltip:
      'Contracted goal for the line item as pushed from Operative. For CPM billing, this is a quantity value. For flat billing (SOV lines), this is a percentage of supply. The value shown is based on impressions or completions, depending on the Billable Event.',
  },
  [LineItemColumn.MetricsCompletions]: {
    header: 'Completions',
    tooltip: 'Number of completions that have been delivered. Completions are triggered at end of ad.',
  },
  [LineItemColumn.MetricsScheduledPercentPacing]: {
    header: 'Scheduled % Pacing',
    tooltip:
      'Shows whether the line is delivering on pace with its scheduled delivery plan. Calculated using 1P gross delivery and the Buffered Goal, accounting for flight schedules, dark days, and other custom schedule settings.',
  },
  [LineItemColumn.MetricsPercentPacing]: {
    header: '% Pacing',
    tooltip: 'Percent delivered vs. percent expected.',
  },
  [LineItemColumn.MetricsPercentExpected]: {
    header: '% Expected',
    tooltip:
      'Percent the ad server expects to have delivered at that moment in time in order for the line item to fulfill the delivery goal.',
  },
  [LineItemColumn.MetricsPercentCompletions]: {
    header: '% Delivered',
    tooltip: '% delivered against the impression or completion goal. Based on billable event.',
  },
  [LineItemColumn.PacingType]: {
    header: 'Pacing Type',
    tooltip:
      'Completions distribution for the line item: Even = consistent delivery. Yield = serves if guaranteed lines are on pace and inventory is sufficient. AFAP = fulfills delivery goal as quickly as possible. SOV = delivers as fast as possible until it reaches % goal.',
  },
  [LineItemColumn.Tier]: {
    header: 'Tier Name',
    tooltip:
      'Controls how the ad server handles delivery of the line item and whether we restrict pacing, factors into priority value.',
  },
  [LineItemColumn.YodaThreshold]: {
    header: 'Yoda Threshold',
    tooltip:
      'The minimum delivery percentage derived from the Priority value for the line. This threshold is defined by the business and enables the ad server to determine when Biddable can compete with the line.',
  },
  [LineItemColumn.YodaPacingStatus]: {
    header: 'Yoda Pacing Status',
    tooltip:
      'Under pacing: % delivered < YODA Threshold. On pace: % delivered between YODA Threshold and Frontload. Overpacing: % delivered > YODA Threshold + Frontload.',
  },
  [LineItemColumn.PriorityName]: {
    header: 'Priority Name',
    tooltip: 'Ranking within a priority tier: high, standard, low.',
  },
  [LineItemColumn.PriorityValue]: {
    header: 'Priority Value',
    tooltip: 'Number calculated based on the priority tier and priority name combination.',
  },
  [LineItemColumn.AdProduct]: {
    header: 'Ad Product',
    tooltip: 'Type of ad that can be trafficked on the line as defined in the Operative package.',
  },
  [LineItemColumn.FrequencyCapList]: {
    header: 'Frequency Cap',
    tooltip: 'The limit for how many times a line item can be served within a given timeframe.',
  },
  [LineItemColumn.BrandCapping]: {
    header: 'Brand Capping',
  },
  [LineItemColumn.AutomateBuffer]: {
    header: 'Buffer Opt-In',
    tooltip:
      'Toggle set to On (green) indicates that an automated buffer has been set on the line item. Switch the toggle to Off (red) if you want it opted out.',
  },
  [LineItemColumn.DeliveryBufferPercent]: {
    header: 'Buffer',
    tooltip:
      'Buffer value is the percentage the line item can over deliver in order to meet the completion goal given potential third-party data discrepancies. Automated buffers are set on line items by default using machine learning to define the optimal value. Account managers can opt out of automated buffers and define a buffer value manually.',
  },
  [LineItemColumn.FrontLoadPercent]: {
    header: 'Frontload',
    tooltip:
      'A delivery ceiling that allows your line item to over-pace up to the frontloaded percentage to take advantage of open inventory.',
  },
  [LineItemColumn.DeliveryForensics]: {
    header: 'DF',
    tooltip: 'Click graph icon to launch Delivery Forensics for the line item.',
  },
  [LineItemColumn.AccessorId]: {
    header: 'Line Item ID',
    tooltip: 'External ID for the line item shared with third-party vendors and populated in tag macros.',
  },
  [LineItemColumn.MetricsRemainingExpectedCompletionsAcc]: {
    header: 'Delivery Remaining',
    tooltip:
      'Amount left to deliver against the contracted goal. Based on impressions or completions, depending on the line’s Billable Event.',
  },
  [LineItemColumn.MetricsCompletionsAtRisk]: {
    header: 'Delivery at Risk',
    tooltip:
      'Estimated amount the ad server may not deliver based on current pacing. Calculated using the line’s Billable Event, whether the line is billed on impressions or completions.',
  },
  [LineItemColumn.MetricsImpressions]: {
    header: 'Impressions',
    tooltip:
      'Number of impressions that have been delivered. An impression indicates the ad started playing on the viewer device.',
  },
  [LineItemColumn.Revenue]: {
    header: 'Net Revenue',
    tooltip: 'The net revenue contracted against the line.',
  },
  [LineItemColumn.MetricsRevenueAtRisk]: {
    header: 'Revenue at Risk',
    tooltip: 'The calculated dollar amount we may not fulfill based on current underpacing.',
  },
  [LineItemColumn.BillableThirdParty]: {
    header: 'Billable Third Party',
    tooltip: '3rd party vendor whose data we use for billing purposes.',
  },
  [LineItemColumn.BillableEvent]: {
    header: 'Billable Event',
    tooltip:
      'Indicates whether billing is based on impressions (ad started playing) or completions (ad viewed till end).',
  },
  [LineItemColumn.UnitCost]: {
    header: 'Net CPM',
    tooltip:
      'The net CPM (unit cost) contracted against the line. Some currencies display in whole units, while sorting uses the exact amount, so rounded values can appear out of order.',
  },
  [LineItemColumn.MetricsUnderPacingRevenue]: {
    header: 'Under-pacing Revenue',
    tooltip: 'How much revenue we have not fulfilled vs. what was expected, at the current point in time.',
  },
  [LineItemColumn.LineOmsLinkId]: {
    header: 'Line OMS ID',
    tooltip: 'Corresponds to the Operative ID for the line.',
  },
  [LineItemColumn.CreatedAt]: {
    header: 'Created Date',
    tooltip: 'The date the line item was pushed from Operative.',
  },
  [LineItemColumn.UpdatedAt]: {
    header: 'Updated Date',
    tooltip: 'Most recent date line changes were pushed from Operative.',
  },
  [LineItemColumn.CreativeMaxSeconds]: {
    header: 'Max Duration',
    tooltip: 'Specifies how long the creative asset can be, as defined in Operative package.',
  },
  [LineItemColumn.CampaignName]: {
    header: 'Campaign Name',
    tooltip: 'The name of the campaign that the line item belongs to.',
  },
  [LineItemColumn.DeliveryGoal]: {
    header: 'Delivery Goal',
    tooltip: 'Contracted goal plus buffer value.',
  },
  [LineItemColumn.IsCoppaOrCaru]: {
    header: 'Needs Advisor Review',
    tooltip: 'A Yes value indicates that the line item requires Advisor approval before trafficking.',
  },
  [LineItemColumn.IsAddedValue]: {
    header: 'Is Added Value?',
    tooltip:
      'A Yes value in this column indicates that the line item was created to give the advertiser free or bonus impressions to encourage them to buy with us.',
  },
  [LineItemColumn.IsMakegood]: {
    header: 'Is Makegood?',
    tooltip:
      'A Yes value in this column indicates that the line item was created to make up some delivery issue to the advertiser.',
  },
  [LineItemColumn.IsFiller]: {
    header: 'Filler Ad',
    tooltip:
      'A Yes value indicates the line contains ads that are used to fill empty slots within a live pod. This ad product is managed by Live Tech Ops, and only available via the O1 Filler package.',
  },
  [LineItemColumn.DeliverableLineItemBuyerName]: {
    header: 'Buyer Name',
    tooltip:
      "The name of the demand entity within a DSP's platform that is responsible for fills. It may correspond to an advertiser, brand, or agency.",
  },
  [LineItemColumn.DeliverableLineItemBuyerId]: {
    header: 'Buyer Seat ID',
    tooltip:
      "The DSP-provided code of a demand entity within a DSP's own platform that is responsible for filling bid requests.",
  },
  [LineItemColumn.DspName]: {
    header: 'DSP Name',
    tooltip: 'The name of the entity that will bid on Programmatic Guaranteed deals.',
  },
  [LineItemColumn.DealId]: {
    header: 'Deal ID',
    tooltip:
      'The ID sent to DSPs in openRTB requests so that they know the parameters of the commercial agreement when bidding.',
  },
  [LineItemColumn.SSP]: {
    header: 'SSP',
    tooltip: 'Indicates the sell-side platform where the line item originated.',
  },
  [LineItemColumn.CampaignTraffickerEmail]: {
    header: 'Account Manager',
    tooltip:
      'The Ad Ops Account Manager responsible for managing campaign setup and delivery fulfillment. This value is pushed from Operative.One.',
  },
  [LineItemColumn.CampaignOrderSalespersonEmail]: {
    header: 'Account Executive',
    tooltip: 'The primary salesperson on the campaign.',
  },
  [LineItemColumn.Currency]: {
    header: 'Currency Type',
    tooltip: 'The currency symbol that the line item will be billed in.',
  },
  [LineItemColumn.Sequence]: {
    header: 'Sequence',
    tooltip: 'Whether the line contains ads that are set up to be delivered in sequence.',
  },
  [LineItemColumn.CustomPacingApplied]: {
    header: 'CP Applied',
    tooltip:
      'On (green) indicates that a custom pacing schedule is set for the line item. Switch to Off (red) to remove the schedule.',
  },
  [LineItemColumn.CustomPacingEligible]: {
    header: 'CP Eligible',
    tooltip: 'A Yes value indicates that the line item is eligible for custom pacing.',
  },
  [LineItemColumn.CustomPacingRequested]: {
    header: 'CP Requested',
    tooltip: 'Indicates whether the line item was manually marked as eligible for custom pacing.',
  },
  [LineItemColumn.CostMethod]: {
    header: 'Cost Method',
    tooltip:
      'Specifies how revenue is calculated, either flat rate or CPM. Flat rate means a single cost for the line. CPM (cost per mille) means the cost is calculated per thousand completions.',
  },
  [LineItemColumn.DistributionPartnerList]: {
    header: 'Distribution Partner',
  },
};

/** Column headers/tooltips when `adsPacingStatusAlignment` is enabled. */
export const LineItemPacingStatusAlignmentColumnsMeta: Partial<
  Record<string, { header: string; tooltip?: string; subHeader?: string }>
> = {
  [LineItemColumn.GeneralPacingStatus]: {
    header: 'Pacing Status',
    tooltip:
      'Indicates whether the line item is on schedule to fulfill the delivery goal. For ended lines, compares billable net delivery with the Booked Goal. If required 3P delivery data is not yet available, the status displays 3P Settling or 3P Unknown based on the applicable settling window.',
  },
  [LineItemColumn.MetricsCompletions]: {
    header: 'Completions',
    tooltip: 'Total first-party completions after filtering invalid traffic.',
  },
  [LineItemColumn.MetricsPercentPacing]: {
    header: '% Pacing',
    tooltip:
      'Shows pacing against straight-line flight progress without adjusting for flight schedules, dark days, or other custom schedule settings. Calculated as Buffered Delivered % divided by elapsed flight time %.',
  },
  [LineItemColumn.MetricsPercentExpected]: {
    header: '% Expected',
    tooltip:
      'Percent the ad server expects to have delivered at that moment in time in order for the line item to fulfill the delivery goal. This metric changes according to flight schedule adjustments.',
  },
  [LineItemColumn.MetricsPercentCompletions]: {
    header: '% Delivered',
    tooltip:
      'Percentage of the Booked Goal delivered using billable net delivery. For 1P-billed lines, this uses 1P net delivery. For 3P-billed lines, this uses 3P net delivery from STAQ. Displays N/A when required 3P delivery data is unavailable.',
  },
  [LineItemColumn.MetricsImpressions]: {
    header: 'Impressions',
    tooltip: 'Total first-party impressions after filtering invalid traffic.',
  },
  [LineItemColumn.MetricsRemainingExpectedCompletionsAcc]: {
    header: 'Contracted Goal Remaining',
    tooltip:
      'Amount left to deliver against the contracted goal. Based on impressions or completions, depending on the line’s Billable Event.',
  },
  [LineItemColumn.MetricsCompletionsAtRisk]: {
    header: 'Buffered Goal at Risk',
    tooltip:
      'Estimated amount the ad server may not deliver based on current pacing. Calculated using the line’s Billable Event, whether the line is billed on impressions or completions. This metric uses buffered goal and changes according to flight schedule changes.',
  },
  [LineItemColumn.DeliveryGoal]: {
    header: 'Buffered Goal',
    tooltip: 'Contracted goal plus buffer value.',
  },
  [LineItemColumn.Revenue]: {
    header: 'Contracted Revenue',
    tooltip: 'The net revenue contracted against the line.',
  },
  [LineItemColumn.MetricsRevenueAtRisk]: {
    header: 'Scheduled Revenue at Risk',
    tooltip: 'The calculated dollar amount we may not fulfill based on current underpacing.',
  },
};
